<template>
  <div v-if="visible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <button @click="close" class="modal-close">
        <i class="fas fa-times"></i>
      </button>

      <div class="modal-body">
        <!-- Course Header -->
        <div class="course-header">
          <div v-if="course.thumbnail" class="course-thumbnail">
            <img :src="getThumbnailUrl(course.thumbnail)" :alt="course.title" />
          </div>

          <div class="header-content">
            <div class="course-badges">
              <span class="course-type">{{ formatCourseType(course.course_type) }}</span>
              <span class="difficulty" :class="`difficulty-${course.difficulty_level?.toLowerCase()}`">
                {{ course.difficulty_level }}
              </span>
            </div>

            <h2 class="course-title">{{ course.title }}</h2>
            <p class="course-description">{{ course.short_description }}</p>

            <div class="course-meta">
              <div class="meta-item" v-if="instructorLabel">
                <i class="fas fa-user"></i>
                <span>{{ instructorLabel }}</span>
              </div>
              <div class="meta-item" v-if="course.estimated_duration_hours">
                <i class="fas fa-clock"></i>
                <span>{{ formatDuration(course.estimated_duration_hours) }}</span>
              </div>
              <div class="meta-item" v-if="course.average_rating">
                <i class="fas fa-star"></i>
                <span>{{ course.average_rating }} / 5.0</span>
              </div>
              <div class="meta-item">
                <i class="fas fa-users"></i>
                <span>{{ course.enrollment_count || 0 }} enrolled</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Course Details -->
        <div class="course-details">
          <div v-if="course.learning_objectives && course.learning_objectives.length > 0" class="detail-section">
            <h3>What You'll Learn</h3>
            <ul class="objectives-list">
              <li v-for="(objective, index) in course.learning_objectives" :key="index">
                <i class="fas fa-check-circle"></i>
                {{ objective }}
              </li>
            </ul>
          </div>

          <div v-if="course.description" class="detail-section">
            <h3>About This Course</h3>
            <div v-html="course.description"></div>
          </div>

          <div v-if="course.prerequisites" class="detail-section">
            <h3>Prerequisites</h3>
            <p>{{ course.prerequisites }}</p>
          </div>

          <div v-if="course.target_audience" class="detail-section">
            <h3>Who This Course Is For</h3>
            <p>{{ course.target_audience }}</p>
          </div>
        </div>

        <!-- Modal Actions -->
        <div class="modal-actions">
          <button 
            v-if="!course.is_enrolled" 
            @click="handleEnroll" 
            class="btn btn-primary btn-large"
          >
            <i class="fas fa-plus"></i>
            Enroll Now
          </button>
          <router-link 
            v-else
            :to="`/learning/courses/${course.id}`"
            class="btn btn-success btn-large"
          >
            <i class="fas fa-play"></i>
            Continue Learning
          </router-link>
          <router-link 
            :to="`/learning/courses/${course.id}`"
            class="btn btn-secondary"
          >
            <i class="fas fa-info-circle"></i>
            View Full Details
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CourseDetailsModal',
  props: {
    course: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'enroll'],
  computed: {
    // Phase 1b — prefer the structured FK list, fall back to legacy text.
    instructorLabel() {
      const list = Array.isArray(this.course?.instructors) ? this.course.instructors : []
      if (list.length) return list[0].full_name
      return this.course?.instructor_name || ''
    },
  },
  methods: {
    close() {
      this.$emit('close')
    },
    handleOverlayClick() {
      this.close()
    },
    handleEnroll() {
      this.$emit('enroll', this.course)
    },
    formatCourseType(type) {
      if (!type) return ''
      return type.replace('_', ' ')
    },
    formatDuration(hours) {
      if (!hours) return 'Duration not specified'
      if (hours < 1) return `${Math.round(hours * 60)} minutes`
      return `${hours} hour${hours !== 1 ? 's' : ''}`
    },
    getThumbnailUrl(thumbnail) {
      if (!thumbnail) return ''
      if (thumbnail.startsWith('http')) return thumbnail
      return `${process.env.VUE_APP_API_URL || ''}/media/${thumbnail}`
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: var(--border-radius);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-close {
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  color: var(--color-text-secondary);
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modal-close:hover {
  background: #f8f9fa;
  color: var(--color-text-primary);
  transform: scale(1.1);
}

.modal-body {
  padding: var(--spacing-xl);
}

/* Course Header */
.course-header {
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-xl);
  border-bottom: 2px solid var(--color-border);
}

.course-thumbnail {
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.course-thumbnail img {
  width: 100%;
  height: auto;
  display: block;
}

.header-content {
  display: flex;
  flex-direction: column;
}

.course-badges {
  display: flex;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
}

.course-type,
.difficulty {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.course-type {
  background: #e3f2fd;
  color: #1976d2;
}

.difficulty {
  background: #f3e5f5;
  color: #7b1fa2;
}

.difficulty-beginner {
  background: #e8f5e9;
  color: #388e3c;
}

.difficulty-intermediate {
  background: #fff3e0;
  color: #f57c00;
}

.difficulty-advanced {
  background: #fce4ec;
  color: #c2185b;
}

.course-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-md) 0;
  line-height: 1.3;
}

.course-description {
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--spacing-lg) 0;
  line-height: 1.6;
}

.course-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
}

.meta-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: 0.9rem;
}

.meta-item i {
  color: var(--color-primary);
}

/* Course Details */
.course-details {
  margin-bottom: var(--spacing-xl);
}

.detail-section {
  margin-bottom: var(--spacing-xl);
}

.detail-section h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 var(--spacing-md) 0;
}

.detail-section p {
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0;
}

.objectives-list {
  list-style: none;
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.objectives-list li {
  padding: var(--spacing-sm) 0;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  color: var(--color-text-secondary);
  line-height: 1.6;
  list-style: none;
  list-style-type: none;
}

.objectives-list li::before {
  content: none;
}

.objectives-list li i {
  color: #4caf50;
  margin-top: 4px;
  flex-shrink: 0;
}

/* Modal Actions */
.modal-actions {
  display: flex;
  gap: var(--spacing-md);
  padding-top: var(--spacing-lg);
  border-top: 2px solid var(--color-border);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius);
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-large {
  padding: var(--spacing-md) var(--spacing-xl);
  font-size: 1.1rem;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--color-secondary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-success {
  background: #4caf50;
  color: white;
}

.btn-success:hover {
  background: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.btn-secondary {
  background: white;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
}

.btn-secondary:hover {
  background: var(--color-primary);
  color: white;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    max-height: 95vh;
  }

  .modal-body {
    padding: var(--spacing-lg);
  }

  .course-header {
    grid-template-columns: 1fr;
  }

  .course-thumbnail {
    max-width: 100%;
  }

  .course-title {
    font-size: 1.5rem;
  }

  .modal-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>

